import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { refreshAdminNotifications } from "@/components/NotificationBell";

const RES_OPTIONS = [
  { id: "MASTER_MAPPED", label: "기존 개발항목으로 연결" },
  { id: "CUSTOM_PRICED", label: "기술검토 후 금액 확정" },
  { id: "EXCLUDED", label: "이번 견적에서 제외" },
  { id: "INCLUDED_IN_SCOPE", label: "다른 항목에 포함" },
  { id: "NEEDS_MORE_REVIEW", label: "추가 검토 필요" },
];

function won(n) {
  return `₩ ${Number(n || 0).toLocaleString()}`;
}

function statusLabel(status) {
  return ({
    PENDING: "기술검토 필요",
    IN_REVIEW: "기술검토 진행 중",
    RESOLVED: "기술검토 완료",
    NOT_REQUIRED: "해당 없음",
  }[status] || status || "-");
}

export default function QuoteTechnicalReview({ quote, onUpdated, onOpenQuote }) {
  const qid = quote?.id;
  const needs = quote?.technical_review_status && quote.technical_review_status !== "NOT_REQUIRED";
  const routeNeeds = quote?.route_type === "FULL_CUSTOM_REVIEW" || quote?.route_type === "PLATFORM_REVIEW" || quote?.confirmed_scope_only;
  const [pack, setPack] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!qid || !(needs || routeNeeds || quote?.pricing_kind === "final")) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/quotes/${qid}/technical-review`);
      setPack(r.data);
      setItems((r.data.items || []).map((row) => ({ ...row })));
    } catch (err) {
      if (err?.response?.status !== 404) toast.error(err?.response?.data?.detail || "기술검토 정보를 불러오지 못했습니다.");
      setPack(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [qid]);

  if (!(needs || routeNeeds || quote?.pricing_kind === "final" || quote?.final_quote_id)) return null;

  const localComplete = items.every((row) => row.resolution && row.resolution !== "NEEDS_MORE_REVIEW");

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((row) => (row.item_id === id ? { ...row, ...patch } : row)));
  };

  const save = async ({ requestReview = false } = {}) => {
    setSaving(true);
    try {
      const r = await api.put(`/admin/quotes/${qid}/technical-review`, {
        resolutions: items,
        request_review: requestReview,
      });
      setPack(r.data);
      setItems((r.data.items || []).map((row) => ({ ...row })));
      if (requestReview) {
        refreshAdminNotifications();
        toast.success(r.data.request_message || "기술검토 요청 완료");
      } else {
        toast.success("기술검토 내용이 저장되었습니다.");
      }
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "저장하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const complete = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/quotes/${qid}/technical-review`, { resolutions: items, request_review: false });
      const r = await api.post(`/admin/quotes/${qid}/technical-review/complete`);
      setPack(r.data);
      setItems((r.data.items || []).map((row) => ({ ...row })));
      refreshAdminNotifications();
      toast.success("기술검토가 완료되었습니다. 이제 최종 견적을 만들 수 있습니다.");
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "기술검토를 완료할 수 없습니다.");
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/quotes/${qid}/finalize`);
      toast.success("최종 견적이 생성되었습니다. 기존 임시 견적 금액과 검토 이력은 그대로 남아 있습니다.");
      refreshAdminNotifications();
      onUpdated?.(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "최종 견적을 만들지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="quote-tech-review" data-testid={`quote-tech-review-${qid}`}>
      <div className="quote-tech-review__head">
        <h5>기술검토</h5>
        <span className="quote-tech-review__status">● {statusLabel(pack?.technical_review_status || quote?.technical_review_status)}</span>
      </div>
      {loading && <p className="text-xs text-white/40">불러오는 중...</p>}
      {pack ? (
        <>
          <div className="quote-tech-review__money">
            <div><span>초기 임시 공급가</span><b>{won(pack.original_provisional_total)}</b></div>
            <div><span>기술검토 조정</span><b>{pack.technical_review_adjustment >= 0 ? "+" : ""}{won(pack.technical_review_adjustment).replace("₩ ", "")}원</b></div>
            <div><span>최종 공급가</span><b>{won(pack.final_supply_price)}</b></div>
          </div>
          {pack.review_request?.open ? (
            <p className="text-xs text-amber-200" data-testid={`quote-tech-requested-${qid}`}>
              기술검토 진행 중
              {pack.review_request.requested_at ? ` · 요청일 ${new Date(pack.review_request.requested_at).toLocaleString("ko-KR")}` : ""}
              {" · 검토 담당: 영업총괄 검토 대기"}
            </p>
          ) : null}
          {quote?.pricing_kind === "final" ? (
            <p className="text-xs text-emerald-300">이 문서는 최종 견적입니다.{quote?.source_provisional_quote_id ? " 임시 견적에서 최종 확정됨." : ""}</p>
          ) : quote?.final_quote_id ? (
            <p className="text-xs text-emerald-300">최종 견적이 이미 생성되었습니다. 이 화면은 임시 견적 이력입니다.</p>
          ) : (
            <p className="text-xs text-white/45">미산정 항목 {items.length}건. 모든 항목을 처리해야 최종 견적을 만들 수 있습니다.</p>
          )}
          {(quote?.final_quote_id || quote?.source_provisional_quote_id) ? (
            <div className="flex flex-wrap gap-2 mb-2">
              {quote?.final_quote_id ? (
                <button type="button" className="admin-row__btn" onClick={() => onOpenQuote?.(quote.final_quote_id)}>최종 견적 보기</button>
              ) : null}
              {quote?.source_provisional_quote_id ? (
                <button type="button" className="admin-row__btn" onClick={() => onOpenQuote?.(quote.source_provisional_quote_id)}>원본 임시 견적 보기</button>
              ) : null}
            </div>
          ) : null}
          {items.map((row) => (
            <div key={row.item_id} className="quote-tech-review__item" data-testid={`quote-tech-item-${row.item_id}`}>
              <strong>{row.label}</strong>
              <p className="hint">현재 상태: {RES_OPTIONS.find((o) => o.id === row.resolution)?.label || row.resolution}</p>
              <div className="quote-tech-review__choices">
                {RES_OPTIONS.map((opt) => (
                  <label key={opt.id}>
                    <input
                      type="radio"
                      name={`res-${row.item_id}`}
                      checked={row.resolution === opt.id}
                      disabled={saving || quote?.pricing_kind === "final" || Boolean(quote?.final_quote_id) || (!pack.can_price && opt.id !== "NEEDS_MORE_REVIEW")}
                      onChange={() => updateItem(row.item_id, { resolution: opt.id })}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
              {row.resolution === "MASTER_MAPPED" && (
                <select
                  value={(row.mapped_master_ids || [])[0] || ""}
                  disabled={saving || !pack.can_price}
                  onChange={(e) => updateItem(row.item_id, { mapped_master_ids: e.target.value ? [e.target.value] : [] })}
                >
                  <option value="">연결할 기능 선택</option>
                  {(pack.mappable_masters || []).map((m) => (
                    <option key={m.id} value={m.code || m.id}>
                      {m.sales_label}{m.code ? ` (${m.code})` : ""}
                    </option>
                  ))}
                </select>
              )}
              {row.resolution === "CUSTOM_PRICED" && (
                <div className="quote-tech-review__fields">
                  <input placeholder="고객용 기능명 (예: ERP 시스템 연동 구축)" value={row.customer_label || ""} disabled={!pack.can_price || saving} onChange={(e) => updateItem(row.item_id, { customer_label: e.target.value })} />
                  <textarea placeholder="고객용 설명 (데이터 연동 구조, 동기화, 테스트 등)" value={row.customer_description || ""} disabled={!pack.can_price || saving} onChange={(e) => updateItem(row.item_id, { customer_description: e.target.value })} />
                  <textarea placeholder="내부 기술 범위 설명" value={row.internal_scope || ""} disabled={!pack.can_price || saving} onChange={(e) => updateItem(row.item_id, { internal_scope: e.target.value })} />
                  <input type="number" min="1" placeholder="공급가" value={row.custom_price || ""} disabled={!pack.can_price || saving} onChange={(e) => updateItem(row.item_id, { custom_price: e.target.value ? Number(e.target.value) : null })} />
                  <textarea placeholder="검토 메모" value={row.note || ""} disabled={!pack.can_price || saving} onChange={(e) => updateItem(row.item_id, { note: e.target.value })} />
                </div>
              )}
              {row.resolution === "EXCLUDED" && (
                <textarea placeholder="제외 사유 (고객과 협의한 내용)" value={row.note || ""} disabled={!pack.can_price || saving} onChange={(e) => updateItem(row.item_id, { note: e.target.value })} />
              )}
              {row.resolution === "INCLUDED_IN_SCOPE" && (
                <div className="quote-tech-review__fields">
                  <input placeholder="포함되는 대상 항목" value={row.included_in_label || ""} disabled={!pack.can_price || saving} onChange={(e) => updateItem(row.item_id, { included_in_label: e.target.value })} />
                  <textarea placeholder="포함 근거" value={row.note || ""} disabled={!pack.can_price || saving} onChange={(e) => updateItem(row.item_id, { note: e.target.value })} />
                </div>
              )}
              {(row.errors || []).length ? <p className="quote-tech-review__err">{row.errors.join(" ")}</p> : null}
            </div>
          ))}
          {!pack.can_price ? <p className="text-xs text-amber-200">금액 확정과 최종 견적 생성은 영업총괄/최고관리자만 할 수 있습니다. 검토 요청은 가능합니다.</p> : null}
          {quote?.pricing_kind !== "final" && !quote?.final_quote_id ? (
            <div className="quote-tech-review__actions">
              {pack.can_request_review ? (
                <button type="button" className="admin-row__btn" disabled={saving} onClick={() => save({ requestReview: true })} data-testid="quote-tech-request">검토 요청</button>
              ) : null}
              <button type="button" className="admin-row__btn" disabled={saving || !pack.can_price} onClick={() => save({})}>저장</button>
              <button type="button" className="admin-row__btn" disabled={saving || !pack.can_price || !localComplete || pack.technical_review_status === "RESOLVED"} onClick={complete} data-testid="quote-tech-complete">기술검토 완료</button>
              <button type="button" className="bg-white text-black rounded-lg px-3 py-1.5 text-xs" disabled={saving || !pack.can_finalize} onClick={finalize} data-testid="quote-tech-finalize">최종 견적 생성</button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
